// Meeting notes on a project page: a list of notes and one dialog to write or
// open a note. Each action item can be turned into a task in the project
// (once); the item then links to its task.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { DatePicker } from '../../components/ui/date-picker'
import { Field } from '../../components/ui/field'
import { Input } from '../../components/ui/input'
import { Select } from '../../components/ui/select'
import { parsePlainDate, type PlainDate } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import {
  addNoteItem,
  createNote,
  deleteNote,
  deleteNoteItem,
  getNote,
  listAssignees,
  listNotes,
  noteItemToTask,
  staffTaskKeys,
  updateNote,
  type MeetingNote,
  type MeetingNoteItemInput,
  type TaskPerson,
} from './api'
import { AssigneePicker } from './AssigneePicker'
import { formatDay, taskDetailRoute, type TaskShell } from './lib'
import { TEXTAREA_CLASS } from './TaskFormDialog'

const SURFACE =
  'rounded-[var(--surface-lifted-radius)] border border-[var(--surface-lifted-border)] bg-[var(--surface-lifted-bg)] p-4 shadow-[var(--surface-lifted-shadow)]'

type Open = { mode: 'new' } | { mode: 'edit'; noteId: string } | null

export function MeetingNotesSection({ projectId, shell }: { projectId: string; shell: TaskShell }) {
  const t = useT(staffTasksI18n)
  const n = t.notes
  const [open, setOpen] = useState<Open>(null)
  const notes = useQuery({
    queryKey: staffTaskKeys.notes(projectId),
    queryFn: () => listNotes(projectId),
  })
  const items = notes.data?.items ?? []

  return (
    <section className={SURFACE} aria-labelledby="staff-meeting-notes">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id="staff-meeting-notes" className="m-0 text-sm font-semibold">
          {n.title}
        </h2>
        <Button size="sm" variant="outline" onClick={() => setOpen({ mode: 'new' })}>
          <Plus className="mr-1 h-4 w-4" aria-hidden />
          {n.add}
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="m-0 text-sm text-[var(--color-text-muted)]">{n.empty}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {items.map((note) => (
            <li key={note.id} className="border-b border-[var(--color-border)] last:border-b-0">
              <button
                type="button"
                onClick={() => setOpen({ mode: 'edit', noteId: note.id })}
                className="flex w-full flex-wrap items-baseline gap-x-3 gap-y-0.5 py-2 text-left hover:bg-[var(--color-surface-muted)]"
                data-testid={`staff-meeting-note-${note.id}`}
              >
                <span className="text-sm font-medium text-[var(--color-text)]">{note.title}</span>
                {note.meeting_date && (
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatDay(note.meeting_date)}
                  </span>
                )}
                <span className="text-xs text-[var(--color-text-muted)]">
                  {n.items(note.open_item_count, note.item_count)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      <Dialog open={open !== null} onOpenChange={(value) => !value && setOpen(null)}>
        {open?.mode === 'new' && (
          <NewNoteForm
            projectId={projectId}
            onCreated={(noteId) => setOpen({ mode: 'edit', noteId })}
            onClose={() => setOpen(null)}
          />
        )}
        {open?.mode === 'edit' && (
          <NoteEditor
            projectId={projectId}
            noteId={open.noteId}
            shell={shell}
            onClose={() => setOpen(null)}
          />
        )}
      </Dialog>
    </section>
  )
}

function NewNoteForm({
  projectId,
  onCreated,
  onClose,
}: {
  projectId: string
  onCreated: (noteId: string) => void
  onClose: () => void
}) {
  const t = useT(staffTasksI18n)
  const n = t.notes
  const queryClient = useQueryClient()
  const people = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const [title, setTitle] = useState('')
  const [date, setDate] = useState<PlainDate | null>(null)
  const [attendees, setAttendees] = useState<string[]>([])
  const [body, setBody] = useState('')
  const [items, setItems] = useState<MeetingNoteItemInput[]>([])

  const save = useMutation({
    mutationFn: () =>
      createNote(projectId, {
        title: title.trim(),
        meeting_date: date,
        body: body.trim() || null,
        attendee_user_ids: attendees,
        items,
      }),
    onSuccess: (note) => {
      toast.success(n.toasts.saved)
      void queryClient.invalidateQueries({ queryKey: staffTaskKeys.notes(projectId) })
      onCreated(note.id)
    },
    onError: () => toast.error(t.toasts.error),
  })

  return (
    <DialogContent className="sm:max-w-2xl">
      <form
        className="flex flex-col gap-4"
        data-testid="staff-meeting-note-form"
        onSubmit={(e) => {
          e.preventDefault()
          if (title.trim()) save.mutate()
        }}
      >
        <DialogHeader>
          <DialogTitle>{n.form.createTitle}</DialogTitle>
        </DialogHeader>
        <NoteFields
          people={people.data?.items ?? []}
          title={title}
          onTitle={setTitle}
          date={date}
          onDate={setDate}
          attendees={attendees}
          onAttendees={setAttendees}
          body={body}
          onBody={setBody}
        />
        <div className="flex flex-col gap-2">
          <span className="text-[length:var(--field-label-size)] font-semibold text-[var(--field-label-text)]">
            {n.form.itemsLabel}
          </span>
          {items.map((item, index) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <span className="min-w-0 flex-1">{item.text}</span>
              <button
                type="button"
                aria-label={n.removeItem(item.text)}
                onClick={() => setItems((current) => current.filter((_, i) => i !== index))}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ))}
          <ItemAdder people={people.data?.items ?? []} onAdd={(item) => setItems((c) => [...c, item])} />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t.form.cancel}
          </Button>
          <Button type="submit" disabled={!title.trim() || save.isPending}>
            {n.form.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}

function NoteFields({
  people,
  title,
  onTitle,
  date,
  onDate,
  attendees,
  onAttendees,
  body,
  onBody,
}: {
  people: TaskPerson[]
  title: string
  onTitle: (value: string) => void
  date: PlainDate | null
  onDate: (value: PlainDate | null) => void
  attendees: string[]
  onAttendees: (value: string[]) => void
  body: string
  onBody: (value: string) => void
}) {
  const t = useT(staffTasksI18n)
  const n = t.notes
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
        <Field id="staff-meeting-note-title" label={n.form.titleLabel} required>
          <Input
            autoFocus
            maxLength={200}
            value={title}
            placeholder={n.form.titlePlaceholder}
            onChange={(e) => onTitle(e.target.value)}
          />
        </Field>
        <DatePicker label={n.form.dateLabel} value={date} onChange={onDate} />
      </div>
      <Field id="staff-meeting-note-attendees" label={n.form.attendeesLabel}>
        <AssigneePicker
          id="staff-meeting-note-attendees"
          people={people}
          value={attendees}
          onChange={onAttendees}
        />
      </Field>
      <Field id="staff-meeting-note-body" label={n.form.bodyLabel}>
        <textarea
          rows={5}
          maxLength={20000}
          className={TEXTAREA_CLASS}
          value={body}
          onChange={(e) => onBody(e.target.value)}
        />
      </Field>
    </>
  )
}

function ItemAdder({
  people,
  onAdd,
}: {
  people: TaskPerson[]
  onAdd: (item: MeetingNoteItemInput) => void
}) {
  const t = useT(staffTasksI18n)
  const n = t.notes
  const [text, setText] = useState('')
  const [person, setPerson] = useState('')
  const [date, setDate] = useState<PlainDate | null>(null)
  const add = () => {
    if (!text.trim()) return
    onAdd({ text: text.trim(), assignee_user_id: person || null, due_date: date })
    setText('')
    setPerson('')
    setDate(null)
  }
  return (
    <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
      <Input
        className="sm:col-span-3"
        value={text}
        maxLength={300}
        placeholder={n.form.itemPlaceholder}
        aria-label={n.form.itemPlaceholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            add()
          }
        }}
      />
      <Select aria-label={n.form.itemPerson} value={person} onChange={(e) => setPerson(e.target.value)}>
        <option value="">{n.form.itemPerson}</option>
        {people.map((p) => (
          <option key={p.user_id} value={p.user_id}>
            {p.display_name}
          </option>
        ))}
      </Select>
      <DatePicker label={n.form.itemDate} value={date} onChange={setDate} />
      <Button type="button" variant="outline" disabled={!text.trim()} onClick={add}>
        {n.form.addItem}
      </Button>
    </div>
  )
}

function NoteEditor({
  projectId,
  noteId,
  shell,
  onClose,
}: {
  projectId: string
  noteId: string
  shell: TaskShell
  onClose: () => void
}) {
  const note = useQuery({
    queryKey: staffTaskKeys.note(projectId, noteId),
    queryFn: () => getNote(projectId, noteId),
  })
  if (!note.data) return <DialogContent className="sm:max-w-2xl" aria-busy />
  return <NoteEditorForm note={note.data} shell={shell} onClose={onClose} />
}

function NoteEditorForm({
  note,
  shell,
  onClose,
}: {
  note: MeetingNote
  shell: TaskShell
  onClose: () => void
}) {
  const t = useT(staffTasksI18n)
  const n = t.notes
  const queryClient = useQueryClient()
  const people = useQuery({ queryKey: staffTaskKeys.assignees, queryFn: listAssignees })
  const [title, setTitle] = useState(note.title)
  const [date, setDate] = useState<PlainDate | null>(
    note.meeting_date ? parsePlainDate(note.meeting_date) : null,
  )
  const [attendees, setAttendees] = useState(note.attendees.map((p) => p.user_id))
  const [body, setBody] = useState(note.body ?? '')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const pid = note.project_id
  const refresh = () => void queryClient.invalidateQueries({ queryKey: staffTaskKeys.all })
  const onError = () => toast.error(t.toasts.error)

  const save = useMutation({
    mutationFn: () =>
      updateNote(pid, note.id, {
        title: title.trim(),
        meeting_date: date,
        body: body.trim() || null,
        attendee_user_ids: attendees,
      }),
    onSuccess: () => {
      toast.success(n.toasts.saved)
      refresh()
      onClose()
    },
    onError,
  })
  const addItem = useMutation({
    mutationFn: (item: MeetingNoteItemInput) => addNoteItem(pid, note.id, item),
    onSuccess: refresh,
    onError,
  })
  const removeItem = useMutation({
    mutationFn: (itemId: string) => deleteNoteItem(pid, note.id, itemId),
    onSuccess: refresh,
    onError,
  })
  const toTask = useMutation({
    mutationFn: (itemId: string) => noteItemToTask(pid, note.id, itemId),
    onSuccess: () => {
      toast.success(n.toasts.taskCreated)
      refresh()
    },
    onError,
  })
  const remove = useMutation({
    mutationFn: () => deleteNote(pid, note.id),
    onSuccess: () => {
      toast.success(n.toasts.deleted)
      refresh()
      onClose()
    },
    onError,
  })

  return (
    <DialogContent className="sm:max-w-2xl">
      <form
        className="flex flex-col gap-4"
        data-testid="staff-meeting-note-editor"
        onSubmit={(e) => {
          e.preventDefault()
          if (title.trim()) save.mutate()
        }}
      >
        <DialogHeader>
          <DialogTitle>{n.form.editTitle}</DialogTitle>
        </DialogHeader>
        <NoteFields
          people={people.data?.items ?? []}
          title={title}
          onTitle={setTitle}
          date={date}
          onDate={setDate}
          attendees={attendees}
          onAttendees={setAttendees}
          body={body}
          onBody={setBody}
        />
        <div className="flex flex-col gap-2">
          <span className="text-[length:var(--field-label-size)] font-semibold text-[var(--field-label-text)]">
            {n.form.itemsLabel}
          </span>
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {note.items.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center gap-2 rounded px-1 py-1 text-sm hover:bg-[var(--color-surface-muted)]"
                data-testid={`staff-meeting-note-item-${item.id}`}
              >
                <span className="min-w-0 flex-1">{item.text}</span>
                {item.assignee && (
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {item.assignee.display_name}
                  </span>
                )}
                {item.due_date && (
                  <span className="text-xs text-[var(--color-text-muted)]">
                    {formatDay(item.due_date)}
                  </span>
                )}
                {item.task_id ? (
                  <Link
                    {...taskDetailRoute(shell, item.task_id)}
                    className="text-xs font-medium text-[var(--color-success)] hover:underline"
                  >
                    ✓ {n.becameTask}
                  </Link>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={toTask.isPending}
                    onClick={() => toTask.mutate(item.id)}
                  >
                    {n.toTask}
                  </Button>
                )}
                <button
                  type="button"
                  aria-label={n.removeItem(item.text)}
                  onClick={() => removeItem.mutate(item.id)}
                  className="inline-flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:text-[var(--color-danger)]"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden />
                </button>
              </li>
            ))}
          </ul>
          <ItemAdder people={people.data?.items ?? []} onAdd={(item) => addItem.mutate(item)} />
        </div>
        <DialogFooter>
          {note.can_delete &&
            (confirmDelete ? (
              <Button
                type="button"
                variant="destructive"
                className="mr-auto"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                {n.deleteConfirm}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outlineDestructive"
                className="mr-auto"
                onClick={() => setConfirmDelete(true)}
              >
                {n.delete}
              </Button>
            ))}
          <Button type="button" variant="outline" onClick={onClose}>
            {t.form.cancel}
          </Button>
          <Button type="submit" disabled={!title.trim() || save.isPending}>
            {n.form.save}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
